import React, { useEffect, useMemo, useState } from 'react'
import { useMatch } from 'react-router-dom'
import { connect } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { useNavigate } from 'react-router-dom'

import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'
import { fetchApiData } from '../../actions/api'
import { apiResourceEndpoint, buildRoutePath, routes, WRAP_UP_SLUG } from '../../constants/routes'
import { Link } from 'react-router-dom'

import CharacterChapter from './character-chapter'
import CharacterInterstitial from './character-interstitial'
import CharacterReflection from './character-reflection'
import CharacterWrapUp from './character-wrap-up'
import Breadcrumb from '../elements/breadcrumb'
import NavActions from '../elements/nav-actions'
import { Helmet } from 'react-helmet'
import { clearMusicSource } from '../../reducers/sound'

const selectActiveUrl = state => state.nav.activeUrl
const selectStories = state => state.stories
const selectKeyIssueCategories = state => state.keyIssueCategories
const selectStory = (state, story) => story

const withBaseUrl = (WrappedComponent) => {
    return function (props) {
        let match = useMatch(routes.murphysView.path)
        return <WrappedComponent params={match?.params} {...props} />
    }
}

const makeStory = createSelector(
    [selectActiveUrl, selectStories],
    (activeUrl, stories) => {
        // if active URL ends in a slash, remove it
        let cleanUrl = activeUrl
        if (cleanUrl.slice(-1) === '/') {
            cleanUrl = cleanUrl.slice(0, -1)
        }
        return stories.find(story => cleanUrl.indexOf(story.characterSlug) > -1)
    }
)

const makeCharacterIssues = createSelector(
    [selectKeyIssueCategories, selectStory],
    (keyIssueCategories, story) => {
        if (!story) {
            return null
        }
        return keyIssueCategories.find(ki => {
            return story.characterKeyIssue === ki.uuid
        })
    }
)

const mapStateToProps = (state) => {
    let story = makeStory(state)
    return {
        navState: state.nav,
        characterKeyIssue: makeCharacterIssues(state, story),
        story: story,
        routeParams: null
    }
}

const MurphyStory = ({ dispatch, navState, story, characterKeyIssue, params }) => {
    const chapterCount = story?.chapters?.length || 0
    const hasWrapUp = !!story?.characterWrapUp
    // the wrap-up is the slide after the last chapter, addressed by WRAP_UP_SLUG instead of a number
    const wrapUpIndex = chapterCount + 1
    const lastIndex = hasWrapUp ? wrapUpIndex : chapterCount
    const isWrapUpUrl = params?.chapterId === WRAP_UP_SLUG
    const chapterId = isWrapUpUrl ? wrapUpIndex : parseInt(params?.chapterId, 10) || 1
    const [activeChapter, setActiveChapter] = useState(chapterId)
    const [showWrapUp, setShowWrapUp] = useState(() => {
        if (story?.chapters) {
            return hasWrapUp && chapterId >= wrapUpIndex
        }
        return false
    })
    const componentName = 'Character Story'
    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])
    const navigate = useNavigate()

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            handlePreloadingState()
        } else if (componentState === 'ANIMATING_IN') {
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    useEffect(() => {
        if (chapterId !== activeChapter) {
            setActiveChapter(chapterId)
        }
    }, [chapterId])

    useEffect(() => {
        if (story?.chapters) {
            if (hasWrapUp && activeChapter >= wrapUpIndex) {
                setShowWrapUp(true)
            } else if (showWrapUp) {
                setShowWrapUp(false)
            }
        }
    }, [story, activeChapter])

    // send numbered urls past the end (including the old /13) to the wrap-up slug,
    // and the wrap-up slug to the last chapter when the story has no wrap-up
    useEffect(() => {
        if (!story?.chapters || !params?.chapterId || story.characterSlug !== `/${params.character}`) {
            return
        }
        let target = null
        if (isWrapUpUrl) {
            if (!hasWrapUp) {
                target = chapterCount
            }
        } else if (parseInt(params.chapterId, 10) > chapterCount) {
            target = hasWrapUp ? WRAP_UP_SLUG : chapterCount
        }
        if (target !== null) {
            navigate(buildRoutePath(routes.murphysView.path, { character: params.character, chapterId: target }), { replace: true })
        }
    }, [story, params?.chapterId])

    const handlePreloadingState = async () => {
        dispatch(clearMusicSource())
        dispatch(fetchApiData(apiResourceEndpoint(`stories/${params.character}`, 'LIST')))
            .then(() => {
                dispatch(completePreloading())
            })
    }

    const handleAnimatingInState = async () => {
        dispatch(completeAnimatingIn())
    }

    const handleAnimatingOutState = () => {
        setActiveChapter(1)
        setShowWrapUp(false)
        dispatch(completeAnimatingOut())
    }

    useEffect(() => {
        if (showWrapUp) {
            dispatch(saveNavTheme('grey'))
        }
    }, [showWrapUp])

    const setActiveChapterIndex = (newIndex) => {
        if (newIndex < 1 || newIndex > lastIndex) {
            return
        }
        const newChapterId = hasWrapUp && newIndex === wrapUpIndex ? WRAP_UP_SLUG : newIndex
        navigate(buildRoutePath(routes.murphysView.path, { character: params.character, chapterId: newChapterId }))
    }

    const renderChapters = () => {
        let chapters = []
        story.chapters.forEach((chapter, index) => {
            // previous, current are both active, next is preloading
            // let isActive = activeChapter >= index
            let activeChapterOffset = activeChapter - 1
            let status = 'OFF'
            if (activeChapterOffset === index) {
                status = 'ACTIVE'
            } else if (activeChapterOffset - 1 === index) {
                status = 'PREVIOUS'
            } else if (activeChapterOffset + 1 === index) {
                status = 'NEXT'
            }
            switch (chapter.bundle) {
            case 'story_chapter':
                chapters.push(
                    <CharacterChapter
                        key={index}
                        status={status}
                        characterName={story.characterName}
                        chapter={chapter}
                        characterKeyIssue={characterKeyIssue} />)
                break
            case 'story_interstitial':
                chapters.push(
                    <CharacterInterstitial
                        key={index}
                        status={status}
                        chapter={chapter}
                        characterKeyIssue={characterKeyIssue} />)
                break
            case 'moment_of_reflection':
                chapters.push(
                    <CharacterReflection
                        key={index}
                        status={status}
                        chapter={chapter}
                        characterKeyIssue={characterKeyIssue} />)
                break
            default:
                // noting
                break
            }
        })
        return chapters
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING' || !story) {
        return null
    }

    return (
        <>
            <Helmet>
                <title>{story.pageTitle}</title>
                <meta name="description" content={story.metaDescription} />
                <meta property="og:title" content={story.pageTitle} />
                <meta property="og:description" content={story.metaDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={story.metaImage?.url} />
            </Helmet>
            <Breadcrumb>
                <Link to={routes.murphysIndex.path}>Tales from the East Side</Link> &bull; { story.title }
            </Breadcrumb>
            <div className={`murphys-story ${showWrapUp ? 'show-wrap-up' : 'hide-wrap-up'}`}>
                <NavActions
                    setActiveChapterIndex={setActiveChapterIndex}
                    activeChapter={activeChapter}
                    chapterLength={chapterCount}
                    lastChapter={lastIndex} />
                { renderChapters() }
            </div>
            <div className={`murphys-wrap-up ${showWrapUp ? 'show-wrap-up' : 'hide-wrap-up'}`}>
                <CharacterWrapUp story={story} />
            </div>
        </>
    )
}

export default withBaseUrl(connect(mapStateToProps)(MurphyStory))
